import React, { useState, useContext, useMemo } from 'react';
import {
    Form,
    Input,
    Select,
    Button,
    Radio,
    Checkbox,
    Row,
    Col,
    Upload,
    Typography,
    DatePicker,
    Space,
    Popconfirm
} from "antd";
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from "dayjs";
import { internalApi } from "../../common/axios";
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
    DIALOG_KEY, validPeriodList,
} from "../../shared/utils/enum";
import toast from "react-hot-toast";
import Papa from 'papaparse';
import { compareVersions, isJSON, removeStringWhitespace } from "../../shared/utils/basic";
import SearchDropDown from "../../shared/components/common/SearchDropDown";
import CustomDateTimePicker from "../../shared/components/date/CustomDateTimePicker";
import { AuthContext } from "../../shared/contexts/authContext";
import { buildUrl } from "../../shared/utils/net";
import { CampaignEntity } from "./CampaignEntity";
import { useItemKrTextData } from "../probability/useItemData";
import { useMailTitle } from "../country/useMailTitle";
import { useServerInfo } from "../serverInfo/useServerInfo";

const { Option } = Select;
dayjs.extend(utc);
dayjs.extend(timezone);

const CampaignEditModal = ({ record, handleClose, dialogRef, handleSetDialog }) => {
    const data = new CampaignEntity(record);
    const [rows, setRows] = useState(data);
    const [recipients, setRecipients] = useState(data.pIds);
    const [saving, setSaving] = useState(false);
    const [isCheck, setIsCheck] = useState(!!data.items && data.items.length > 0);
    const [isCheckRegistry, setIsCheckRegistry] = useState(data.registrationStart > 0);
    const [items, setItems] = useState(() => isJSON(rows.items) ? JSON.parse(rows.items) : rows.items);
    const [length, setLength] = useState(recipients ? recipients.split(',').length : 0);

    const { currentUser } = useContext(AuthContext);
    const { data: mailTitle, isLoading: isMailTitleLoading, error: mailTitleError } = useMailTitle();
    const { data: krItemTextData, isLoading: isKrItemTextDataLoading, error: krItemTextDataError } = useItemKrTextData();
    const { data: serverInfoData } = useServerInfo();

    const versionOptions = useMemo(() => {
        const versions = serverInfoData?.serverInfo?.versions;
        if (!versions) return [];
        const all = [...(versions.aos || []), ...(versions.ios || [])].map((v) => v.version).filter(Boolean);
        return [...new Set(all)].sort(compareVersions);
    }, [serverInfoData]);

    const handleCSVUpload = (info) => {
        const file = info.file;
        if (file && file.type === 'text/csv') {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    const rowsArray = results.data.map(row => Object.values(row).toString());
                    handleSetRecipients(rowsArray.join());
                },
                error: (error) => {
                    console.error('Error parsing CSV file:', error);
                }
            });
        } else {
            console.error('Please upload a valid CSV file.');
        }
    };

    const handleSetRecipients = (value) => {
        value = removeStringWhitespace(value);
        setRecipients(value);
        const array = value.split(',');
        const len = value === '' ? 0 : array.length;
        setLength(len);
    }

    const hasDuplicates = () => {
        const array = recipients.split(',');
        const seen = new Set();
        let duplicateCount = 0;
        const duplicateChar = [];
        for (const item of array) {
            if (seen.has(item)) {
                duplicateCount++;
                duplicateChar.push(item);
            }
            seen.add(item);
        }
        if (duplicateCount > 0) {
            toast.error(`중복된 PID가 ${duplicateCount}개 있습니다. (${duplicateChar.join()})`);
        } else {
            toast.success("중복된 PID가 없습니다.");
        }
    }

    const handleSetRows = (name, value) => {
        setRows((prevState) => {
            const updatedData = { ...prevState, [name]: value };
            return new CampaignEntity(updatedData);
        });
    }

    const handleDelete = async () => {
        toast.loading("삭제 중...");
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            await internalApi.delete(buildUrl('campaign', { cId: rows.id })).then((res) => {
                toast.dismiss();
                toast.success(res.data.message);
                handleClose(true);
            }).catch((err) => {
                toast.dismiss();
                toast.error("삭제 실패");
                console.error(err);
            });
            handleSetDialog([{ key: DIALOG_KEY.OPEN, value: false }, { key: DIALOG_KEY.CONFIRM_TEXT, value: '' }]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{ key: DIALOG_KEY.CONFIRM_TEXT, value: '' }]);
        }
    }

    const handleSave = async () => {
        if (saving) return;
        rows.items = items;
        if (rows.isInvalidate()) return;
        toast.loading("저장 중...");
        const pIds = removeStringWhitespace(recipients).split(',');
        rows.pIds = pIds;
        setSaving(true);
        await internalApi.post('campaign', { data: rows.toJson(), pIds }).then((res) => {
            toast.dismiss();
            if (res.data.deliveryFailedUsers.length > 0) {
                window.alert(`전송 실패(${res.data.deliveryFailedUsers.length}명) : ${res.data.deliveryFailedUsers.join()}`);
            } else {
                toast.success(res.data.message);
            }
            handleClose(true);
        }).catch((err) => {
            toast.dismiss();
            console.error(err);
            toast.error("저장 실패");
        }).finally(() => setSaving(false));
        handleSetDialog([{ key: DIALOG_KEY.OPEN, value: false }, { key: DIALOG_KEY.CONFIRM_TEXT, value: '' }]);
    }

    if (isMailTitleLoading || isKrItemTextDataLoading) return <div>텍스트 로딩 중...</div>;
    const firstError = mailTitleError || krItemTextDataError;
    if (firstError) return <div>에러 발생: {firstError.message}</div>;

    return (
        <Form layout="vertical" onFinish={handleSave} className="campaign-edit" style={{ maxHeight: '80vh', overflowY: 'auto', padding: 16 }}>
            <Row gutter={16} className="campaign-edit-param">
                <Col span={24}>
                    <Form.Item label="우편 제목" required>
                        <Select
                            value={rows.title}
                            onChange={v => handleSetRows('title', v)}
                            style={{ width: 750 }}
                            size="middle"
                        >
                            {mailTitle.map((option) => (
                                <Option key={option.index} value={option.index}>
                                    {`idx ${option.index} | ${option.title.find((t) => t.langCode === 'ko').text}`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16} className="campaign-edit-param">
                <Col span={24}>
                    <Form.Item label="수신자 유형" required>
                        <Radio.Group
                            name="recipientType"
                            value={Number(rows.recipientType)}
                            onChange={e => handleSetRows('recipientType', e.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            <Radio.Button value={0}>전체</Radio.Button>
                            <Radio.Button value={1}>개별</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    {Number(rows.recipientType) === 1 && (
                        <div className={'campaign-edit-etc'}>
                            <Space style={{ marginBottom: 8 }}>
                                <Upload
                                    accept=".csv"
                                    showUploadList={false}
                                    beforeUpload={() => false}
                                    onChange={handleCSVUpload}
                                >
                                    <Button icon={<UploadOutlined />}>CSV. 파일 첨부</Button>
                                </Upload>
                                <Button onClick={hasDuplicates}>중복 체크</Button>
                            </Space>
                            <Input.TextArea
                                className="textField"
                                placeholder="PID (중요!! : PID는 콤마(,)로 구분)"
                                value={recipients}
                                onChange={e => handleSetRecipients(e.target.value)}
                                style={{ width: 400, marginTop: 8, height: 100, overflowY: 'auto' }}
                                autoSize={{ minRows: 3, maxRows: 6 }}
                            />
                            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                                입력 계정 : {length}
                            </Typography.Text>
                        </div>
                    )}
                </Col>
            </Row>
            <Row gutter={16} className="campaign-edit-param">
                <Col span={24}>
                    <Form.Item label="대상 버전" required>
                        <Radio.Group
                            name="versionType"
                            value={Number(rows.versionType)}
                            onChange={e => {
                                handleSetRows('versionType', e.target.value);
                                if (Number(e.target.value) === 0) handleSetRows('versions', '');
                            }}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            <Radio.Button value={0}>전체</Radio.Button>
                            <Radio.Button value={1}>지정 버전</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    {Number(rows.versionType) === 1 && (
                        <div className={'campaign-edit-etc'}>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                placeholder="대상 버전 선택"
                                value={rows.versions ? rows.versions.split(',') : []}
                                onChange={v => handleSetRows('versions', [...v].sort(compareVersions).join(','))}
                                style={{ width: 400 }}
                            >
                                {versionOptions.map((v) => (
                                    <Option key={v} value={v}>{v}</Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </Col>
            </Row>
            <Row gutter={16} className="campaign-edit-param">
                <Col span={24}>
                    <Checkbox
                        checked={isCheck}
                        onChange={e => setIsCheck(e.target.checked)}
                        style={{ marginRight: 8 }}
                    >
                        지급 아이템
                    </Checkbox>
                    {isCheck && (
                        <SearchDropDown dataArr={krItemTextData[currentUser.target]} items={items} setItems={setItems} hasQuantity={true} />
                    )}
                </Col>
            </Row>
            <Row gutter={16} className="campaign-edit-param">
                <Col span={24}>
                    <Checkbox
                        checked={isCheckRegistry}
                        onChange={e => {
                            setIsCheckRegistry(e.target.checked);
                            if (!e.target.checked) {
                                rows.registrationReset();
                            }
                        }}
                        style={{ marginRight: 8 }}
                    >
                        계정 생성일 기준
                    </Checkbox>
                    {isCheckRegistry && (
                        <Space>
                            <DatePicker
                                value={rows.registrationStart ? dayjs.unix(rows.registrationStart) : dayjs().startOf('day')}
                                onChange={value => handleSetRows('registrationStart', value ? dayjs(value).startOf('minute').unix() : 0)}
                                showTime={{format: 'HH:mm'}}
                                format="YYYY-MM-DD HH:mm"
                                placeholder="계정 생성일(시작)"
                            />
                            <DatePicker
                                value={rows.registrationEnd ? dayjs.unix(rows.registrationEnd) : dayjs().endOf('day')}
                                onChange={value => handleSetRows('registrationEnd', value ? dayjs(value).endOf('minute').unix() : 0)}
                                showTime={{format: 'HH:mm'}}
                                format="YYYY-MM-DD HH:mm"
                                placeholder="계정 생성일(종료)"
                            />
                        </Space>
                    )}
                </Col>
            </Row>
            <Row gutter={16} className="campaign-edit-param">
                <Col span={24}>
                    <CustomDateTimePicker rows={rows} handleSetRows={handleSetRows} />
                </Col>
            </Row>
            <Row gutter={16} className="campaign-edit-param">
                <Col span={4}>
                    <Form.Item label="유효 기간">
                        <Select
                            value={rows.validPeriod}
                            onChange={v => handleSetRows('validPeriod', v)}
                            style={{ width: 100 }}
                        >
                            {validPeriodList.map((option) => (
                                <Option key={option.value} value={option.value}>{option.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={20}>
                    <Form.Item label="사유">
                        <Input
                            value={rows.memo}
                            onChange={e => handleSetRows('memo', e.target.value)}
                            style={{ width: 630 }}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16} justify="end" className="campaign-buttons">
                <Col>
                    {rows.end > dayjs().unix() && (
                        <Button type="primary" htmlType="submit" loading={saving}>저장</Button>
                    )}
                </Col>
                <Col>
                    <Button onClick={handleClose}>취소</Button>
                </Col>
                <Col>
                    {rows.id && (
                        <Popconfirm
                            title="정말 삭제하시겠습니까? 삭제하려면 'DELETE'를 입력하세요."
                            onConfirm={handleDelete}
                            okText="삭제"
                            cancelText="취소"
                            icon={<DeleteOutlined style={{ color: 'red' }} />}
                            description={<Input onChange={e => handleSetDialog([{ key: DIALOG_KEY.CONFIRM_TEXT, value: e.target.value }])} placeholder="DELETE" />}
                        >
                            <Button danger icon={<DeleteOutlined />}>삭제</Button>
                        </Popconfirm>
                    )}
                </Col>
            </Row>
        </Form>
    );
}

export default CampaignEditModal;
